import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import NavigationBar from "../layout/NavigationBar";
import Footer from "../layout/footer";

function QuestionForm() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const questionId = searchParams.get("id");
  const isEdit = !!questionId;

  const [formData, setFormData] = useState({
    question: "",
    options: [
      { option_text: "", is_correct: false },
      { option_text: "", is_correct: false },
    ],
    category: "general",
    difficulty_level: "easy",
    levelId: "",
    active_status: true,
  });

  const [levelsData, setLevelsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchLevels();
    if (isEdit) {
      fetchQuestionDetails();
    }
  }, [questionId]);

  const fetchLevels = async () => {
    try {
      const response = await axios({
        method: "POST",
        url: process.env.REACT_APP_APIURL,
        headers: { "Content-Type": "application/json" },
        data: JSON.stringify({
          environment: "live",
          apikey: process.env.REACT_APP_APIKEY,
          secret: process.env.REACT_APP_SECRET,
          method: "getAllLevels",
          module: "user",
        }),
      });

      if (response.data.statusCode === 200) {
        setLevelsData(response.data.result);
      }
    } catch (error) {
      console.error("Error fetching levels:", error);
    }
  };

  const fetchQuestionDetails = async () => {
    try {
      const response = await axios({
        method: "POST",
        url: process.env.REACT_APP_APIURL,
        headers: { "Content-Type": "application/json" },
        data: JSON.stringify({
          environment: "live",
          apikey: process.env.REACT_APP_APIKEY,
          secret: process.env.REACT_APP_SECRET,
          method: "getQuestionById",
          module: "question",
          questionId: questionId,
        }),
      });

      if (response.data.statusCode === 200) {
        const question = response.data.result.question;
        setFormData({
          question: question.question,
          options: question.options.length >= 2 ? question.options : [
            { option_text: "", is_correct: false },
            { option_text: "", is_correct: false },
          ],
          category: question.category,
          difficulty_level: question.difficultyLevel,
          levelId: question.levelId || "",
          active_status: question.activeStatus,
        });
      }
    } catch (error) {
      console.error("Error fetching question details:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleOptionChange = (index, field, value) => {
    const newOptions = [...formData.options];
    
    if (field === "is_correct" && value) {
      // If marking as correct, unmark all others
      newOptions.forEach((option, i) => {
        option.is_correct = i === index;
      });
    } else {
      newOptions[index][field] = value;
    }
    
    setFormData(prev => ({ ...prev, options: newOptions }));
    
    // Clear option errors
    if (errors[`option_${index}`]) {
      setErrors(prev => ({ ...prev, [`option_${index}`]: "" }));
    }
  };

  const addOption = () => {
    if (formData.options.length < 6) {
      setFormData(prev => ({
        ...prev,
        options: [...prev.options, { option_text: "", is_correct: false }]
      }));
    }
  };

  const removeOption = (index) => {
    if (formData.options.length > 2) {
      const newOptions = formData.options.filter((_, i) => i !== index);
      setFormData(prev => ({ ...prev, options: newOptions }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.question.trim()) {
      newErrors.question = "Question is required";
    }
    
    // Validate options
    formData.options.forEach((option, index) => {
      if (!option.option_text.trim()) {
        newErrors[`option_${index}`] = "Option text is required";
      }
    });
    
    // Check if at least one option is marked as correct
    const hasCorrectOption = formData.options.some(option => option.is_correct);
    if (!hasCorrectOption) {
      newErrors.correct_answer = "At least one option must be marked as correct";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    
    try {
      const payload = {
        environment: "live",
        apikey: process.env.REACT_APP_APIKEY,
        secret: process.env.REACT_APP_SECRET,
        method: isEdit ? "updateQuestion" : "addQuestion",
        module: "question",
        ...formData,
        created_by: "507f1f77bcf86cd799439011", // Replace with actual user ID from session
      };

      if (isEdit) {
        payload.questionId = questionId;
      }

      const response = await axios({
        method: "POST",
        url: process.env.REACT_APP_APIURL,
        headers: { "Content-Type": "application/json" },
        data: JSON.stringify(payload),
      });

      if (response.data.statusCode === 200) {
        alert(`Question ${isEdit ? 'updated' : 'added'} successfully!`);
        navigate("/questions");
      } else {
        alert(response.data.message || `Failed to ${isEdit ? 'update' : 'add'} question`);
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("An error occurred while processing your request");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="wrapper" id="root">
      <header>
        <NavigationBar />
      </header>

      <div className="content-wrapper">
        <div className="content-header">
          <div className="container-fluid">
            <div className="row mb-2">
              <div className="col-sm-6">
                <h1 className="m-0 txt_lft">
                  {isEdit ? 'Edit Question' : 'Add Question'}
                </h1>
              </div>
              <div className="col-sm-6">
                <button
                  type="button"
                  className="btn btn-secondary float-right"
                  onClick={() => navigate("/questions")}
                >
                  <i className="fa fa-arrow-left mr-2"></i>Back to Questions
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-lg-8">
                <div className="card">
                  <div className="card-header">
                    <h3 className="card-title">Question Details</h3>
                  </div>
                  <form onSubmit={handleSubmit}>
                    <div className="card-body">
                      <div className="form-group">
                        <label htmlFor="question">
                          Question <span className="text-danger">*</span>
                        </label>
                        <textarea
                          className={`form-control ${errors.question ? 'is-invalid' : ''}`}
                          id="question"
                          name="question"
                          rows="3"
                          value={formData.question}
                          onChange={handleInputChange}
                          placeholder="Enter your question here..."
                        />
                        {errors.question && (
                          <div className="invalid-feedback">{errors.question}</div>
                        )}
                      </div>

                      <div className="form-group">
                        <label>
                          Answer Options <span className="text-danger">*</span>
                        </label>
                        {formData.options.map((option, index) => (
                          <div key={index} className="input-group mb-2">
                            <div className="input-group-prepend">
                              <div className="input-group-text">
                                <input
                                  type="radio"
                                  name="correct_answer"
                                  checked={option.is_correct}
                                  onChange={(e) => handleOptionChange(index, 'is_correct', e.target.checked)}
                                  title="Mark as correct answer"
                                />
                              </div>
                            </div>
                            <input
                              type="text"
                              className={`form-control ${errors[`option_${index}`] ? 'is-invalid' : ''}`}
                              value={option.option_text}
                              onChange={(e) => handleOptionChange(index, 'option_text', e.target.value)}
                              placeholder={`Option ${index + 1}`}
                            />
                            {formData.options.length > 2 && (
                              <div className="input-group-append">
                                <button
                                  type="button"
                                  className="btn btn-outline-danger"
                                  onClick={() => removeOption(index)}
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              </div>
                            )}
                            {errors[`option_${index}`] && (
                              <div className="invalid-feedback">{errors[`option_${index}`]}</div>
                            )}
                          </div>
                        ))}
                        {errors.correct_answer && (
                          <div className="text-danger small mb-2">{errors.correct_answer}</div>
                        )}
                        {formData.options.length < 6 && (
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            onClick={addOption}
                          >
                            <i className="fa fa-plus mr-1"></i>Add Option
                          </button>
                        )}
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="category">Category</label>
                            <input
                              type="text"
                              className="form-control"
                              id="category"
                              name="category"
                              value={formData.category}
                              onChange={handleInputChange}
                              placeholder="e.g., General, Sports, History"
                            />
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="difficulty_level">Difficulty Level</label>
                            <select
                              className="form-control"
                              id="difficulty_level"
                              name="difficulty_level"
                              value={formData.difficulty_level}
                              onChange={handleInputChange}
                            >
                              <option value="easy">Easy</option>
                              <option value="medium">Medium</option>
                              <option value="hard">Hard</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6">
                          <div className="form-group">
                            <label htmlFor="levelId">Level</label>
                            <select
                              className="form-control"
                              id="levelId"
                              name="levelId"
                              value={formData.levelId}
                              onChange={handleInputChange}
                            >
                              <option value="">Select Level</option>
                              {levelsData.map((level) => (
                                <option key={level._id} value={level._id}>
                                  {level.level_name}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="form-group">
                            <div className="form-check mt-4">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id="active_status"
                                name="active_status"
                                checked={formData.active_status}
                                onChange={handleInputChange}
                              />
                              <label className="form-check-label" htmlFor="active_status">
                                Active Status
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="card-footer">
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={loading}
                      >
                        {loading ? (
                          <>
                            <i className="fa fa-spinner fa-spin mr-2"></i>
                            {isEdit ? 'Updating...' : 'Adding...'}
                          </>
                        ) : (
                          <>
                            <i className="fa fa-save mr-2"></i>
                            {isEdit ? 'Update Question' : 'Add Question'}
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary ml-2"
                        onClick={() => navigate("/questions")}
                        disabled={loading}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default QuestionForm;